import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { isEqual, last } from 'lodash';

import { ticketDetailsViewTypes } from '@twiliointernal/services-common/containers/TicketDetailsView';
import { SupportApiResponse } from '@twiliointernal/org/twilio/e2-commons/shared/definitions';

import { constants, types } from './definitions';
import { getCollaboratorsFromTicket } from './utils';
import { SupportApi } from '../../clients';
import { useCurrentPlan, useUserDetails } from '../../hooks';
import {
  PERSONALIZED_SUPPORT_PLAN_TAG,
  trackingDetails,
} from '../../definitions/constants';
import logger from '../../../lib/logger';

const useGetTicketDetails = (): types.UseGetTicketDetailsReturn => {
  /**
   * Casting here to avoid react-router TS definitions since ticketId will always
   * be present as an ID when accessing /tickets/:ticketId
   */
  const { ticketId } = useParams<'ticketId'>() as { ticketId: string };
  const currentPlan = useCurrentPlan();
  const { user } = useUserDetails();

  const [loading, toggleLoading] = useState<boolean>(true);
  const [ticketDetails, updateTicketDetails] =
    useState<ticketDetailsViewTypes.TicketDetails>();
  const [collaborators, updateCollaborators] = useState<string[]>([]);
  const [errors, updateErrors] = useState<types.UseGetTicketDetailsErrors>({});

  useEffect(() => {
    const getTicketDetails = async (): Promise<void> => {
      try {
        const response = await SupportApi.getTicketDetails(ticketId);

        if (!response.success) {
          throw new Error(JSON.stringify(response.message));
        }

        updateTicketDetails(response.data);

        const formattedCollaborators = getCollaboratorsFromTicket(
          response.data,
        );

        updateCollaborators(formattedCollaborators);
      } catch (err) {
        handleError(err);
      }
      toggleLoading(false);
    };

    // Set `ticketId` to all logging events while a user is viewing the ticket
    logger.setGlobalContextProperty('ticketId', ticketId);
    logger.info(`${constants.LOG_PREFIX}::[${trackingDetails.viewTicket}]`);

    getTicketDetails();

    return () => {
      // Remove `ticketId` from logging events when component is unmounted
      logger.removeGlobalContextProperty('ticketId');
    };
  }, []);

  const handleError = (error: Error): void => {
    if (error.message.includes('404')) {
      updateErrors({ notFound: true });
    }

    if (error.message.includes('403')) {
      updateErrors({ forbidden: true });
    }

    logger.error(`${constants.LOG_PREFIX}::${error}`);
  };

  if (!ticketDetails) {
    return {
      errors,
      loading,
      ticketId,
    };
  }

  /**
   * TODO: Consolidate user types to global definition
   * - Currently the 'user' in services-common is a mistmatch for the
   * user provided via the API
   * - This method grabs the necessary user props for Ticket Details,
   * but the only used prop is `fullName` for Escalations, so we could
   * likely find an alternate workaround
   */
  const handleGetTicketDetailsUser =
    (): ticketDetailsViewTypes.TicketDetailsViewProps['user'] => {
      if (!user) {
        return constants.userInitalState;
      }

      return {
        email: user.emailAddr,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: `${user.firstName} ${user.lastName}`,
      };
    };

  // Format collaborators from ticket before updating state
  const handleUpdateCollaborators = ({
    responseData,
    newCollaborators,
  }: types.UpdateCollaboratorsArgs): void => {
    if (newCollaborators) {
      updateCollaborators(newCollaborators);
    }

    if (responseData) {
      const formattedCollaborators = getCollaboratorsFromTicket(responseData);
      updateCollaborators(formattedCollaborators);

      updateTicketDetails({
        ...ticketDetails,
        emailCcIds: responseData.emailCcIds,
        users: responseData.users,
      });
    }
  };

  const handleUpdateTicketDetails = (
    response: SupportApiResponse<ticketDetailsViewTypes.TicketDetails>,
  ): void => {
    let newTicketDetails = ticketDetails;
    const { comments, escalation, status } = response?.data;

    const hasStatusChanged = !isEqual(ticketDetails.status, status);
    const wasEscalated = !isEqual(ticketDetails.escalation, escalation);
    const haveCommentsChanges = !isEqual(ticketDetails.comments, comments);

    // Update comments if difference between state <> response
    if (haveCommentsChanges) {
      const newComment = last(comments) as ticketDetailsViewTypes.TicketComment;

      newTicketDetails.comments = [...newTicketDetails.comments, newComment];
    }

    // Update status if present in form data
    if (hasStatusChanged) {
      newTicketDetails.status = status;
    }

    // Update escalations if present in response
    if (wasEscalated) {
      newTicketDetails.escalation = escalation;
    }

    updateTicketDetails(newTicketDetails);
  };

  const hasPersonalizedSupportPlanTag = !!ticketDetails.tags?.includes(
    PERSONALIZED_SUPPORT_PLAN_TAG,
  );

  const hasPersonalizedSupport =
    currentPlan.hasPersonalizedSupportPlan || hasPersonalizedSupportPlanTag;

  return {
    collaborators,
    hasPersonalizedSupport,
    loading,
    ticketDetails,
    ticketId,
    ticketDetailsUser: handleGetTicketDetailsUser(),
    updateCollaborators: handleUpdateCollaborators,
    updateTicketDetails: handleUpdateTicketDetails,
  };
};

export default useGetTicketDetails;
